import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tooltip,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider
} from '@mui/material'
import {
  Visibility,
  FilterList,
  Download,
  Refresh,
  LocalShipping
} from '@mui/icons-material'
import { format } from 'date-fns'
import axios from 'axios'
import { QRCodeSVG } from 'qrcode.react'
import { API_BASE_URL } from '../config/api'

interface Order {
  id: string
  order_number?: string
  buyer_id: string
  buyer_name?: string
  buyer_email?: string
  farmer_id?: string
  farmer_name?: string
  product_name?: string
  quantity: number
  unit: string
  price_per_unit: number
  total_amount: number
  status: string
  payment_status: string
  delivery_status: string
  order_date: string
  delivery_date?: string
  delivery_address?: string
  notes?: string
}

const AllOrders: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([])
  const [filteredOrders, setFilteredOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)
  const [detailsOpen, setDetailsOpen] = useState(false)

  // Filters
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all')
  const [deliveryStatusFilter, setDeliveryStatusFilter] = useState('all')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [minAmount, setMinAmount] = useState('')
  const [maxAmount, setMaxAmount] = useState('')

  // Statistics
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalRevenue: 0,
    pendingOrders: 0,
    completedOrders: 0,
    paidOrders: 0,
    deliveredOrders: 0
  })

  useEffect(() => {
    fetchOrders()
  }, [])

  useEffect(() => {
    applyFilters()
  }, [orders, searchTerm, statusFilter, paymentStatusFilter, deliveryStatusFilter, startDate, endDate, minAmount, maxAmount])

  const fetchOrders = async () => {
    try {
      setLoading(true)
      setError(null)
      const token = localStorage.getItem('token')
      
      const response = await axios.get(`${API_BASE_URL}/orders/admin/list`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      if (response.data.success) {
        setOrders(response.data.data)
        calculateStats(response.data.data)
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch orders')
      console.error('Error fetching orders:', err)
    } finally {
      setLoading(false)
    }
  }

  const calculateStats = (ordersData: Order[]) => {
    const totalOrders = ordersData.length
    const totalRevenue = ordersData.reduce((sum, order) => sum + (Number(order.total_amount) || 0), 0)
    const pendingOrders = ordersData.filter(order => order.status === 'pending').length
    const completedOrders = ordersData.filter(order => order.status === 'completed').length
    const paidOrders = ordersData.filter(order => order.payment_status === 'paid').length
    const deliveredOrders = ordersData.filter(order => order.delivery_status === 'delivered').length

    setStats({
      totalOrders,
      totalRevenue,
      pendingOrders,
      completedOrders,
      paidOrders,
      deliveredOrders
    })
  }

  const applyFilters = () => {
    let filtered = [...orders]

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase()
      filtered = filtered.filter(order =>
        order.product_name?.toLowerCase().includes(search) ||
        order.buyer_name?.toLowerCase().includes(search) ||
        order.buyer_email?.toLowerCase().includes(search) ||
        order.farmer_name?.toLowerCase().includes(search) ||
        order.id.toLowerCase().includes(search)
      )
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => order.status === statusFilter)
    }

    // Payment status filter
    if (paymentStatusFilter !== 'all') {
      filtered = filtered.filter(order => order.payment_status === paymentStatusFilter)
    }

    // Delivery status filter
    if (deliveryStatusFilter !== 'all') {
      filtered = filtered.filter(order => order.delivery_status === deliveryStatusFilter)
    }

    // Date range filter
    if (startDate || endDate) {
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.order_date)
        const start = startDate ? new Date(startDate) : null
        const end = endDate ? new Date(endDate) : null
        
        if (start && end) {
          return orderDate >= start && orderDate <= end
        } else if (start) {
          return orderDate >= start
        } else if (end) {
          return orderDate <= end
        }
        return true
      })
    }

    // Amount filter
    if (minAmount) {
      filtered = filtered.filter(order => order.total_amount >= parseFloat(minAmount))
    }
    if (maxAmount) {
      filtered = filtered.filter(order => order.total_amount <= parseFloat(maxAmount))
    }

    setFilteredOrders(filtered)
    setPage(0)
  }

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleViewDetails = (order: Order) => {
    setSelectedOrder(order)
    setDetailsOpen(true)
  }

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: 'success' | 'info' | 'warning' | 'error' | 'default' } = {
      completed: 'success',
      processing: 'info',
      pending: 'warning',
      cancelled: 'error',
      paid: 'success',
      unpaid: 'warning',
      delivered: 'success',
      shipped: 'info',
      preparing: 'warning'
    }
    return colors[status] || 'default'
  }

  const exportToCSV = () => {
    const headers = ['Order ID', 'Product', 'Buyer', 'Farmer', 'Quantity', 'Price/Unit', 'Total', 'Status', 'Payment', 'Delivery', 'Date']
    const rows = filteredOrders.map(order => [
      order.id,
      order.product_name || 'N/A',
      order.buyer_name || order.buyer_email || 'Unknown',
      order.farmer_name || 'N/A',
      `${order.quantity} ${order.unit}`,
      order.price_per_unit,
      order.total_amount,
      order.status,
      order.payment_status,
      order.delivery_status,
      format(new Date(order.order_date), 'yyyy-MM-dd HH:mm')
    ])

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `orders_${format(new Date(), 'yyyy-MM-dd')}.csv`
    a.click()
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          All Orders Management
        </Typography>
        <Box display="flex" gap={2}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchOrders} color="primary">
              <Refresh />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export to CSV">
            <IconButton onClick={exportToCSV} color="primary">
              <Download />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Total Orders
              </Typography>
              <Typography variant="h5">{stats.totalOrders}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Total Revenue
              </Typography>
              <Typography variant="h5">${Number(stats.totalRevenue || 0).toFixed(2)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Pending
              </Typography>
              <Typography variant="h5" color="warning.main">{stats.pendingOrders}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Completed
              </Typography>
              <Typography variant="h5" color="success.main">{stats.completedOrders}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Paid
              </Typography>
              <Typography variant="h5" color="success.main">{stats.paidOrders}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Delivered
              </Typography>
              <Typography variant="h5" color="success.main">{stats.deliveredOrders}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <FilterList />
          <Typography variant="h6">Filters</Typography>
        </Box>
        <Grid container spacing={2}>
          {/* First Row */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Search"
              placeholder="Product, buyer, farmer, ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Order Status</InputLabel>
              <Select
                value={statusFilter}
                label="Order Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Payment</InputLabel>
              <Select
                value={paymentStatusFilter}
                label="Payment"
                onChange={(e) => setPaymentStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="unpaid">Unpaid</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Delivery</InputLabel>
              <Select
                value={deliveryStatusFilter}
                label="Delivery"
                onChange={(e) => setDeliveryStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="delivered">Delivered</MenuItem>
                <MenuItem value="shipped">Shipped</MenuItem>
                <MenuItem value="preparing">Preparing</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {/* Second Row */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Min $"
              type="number"
              value={minAmount}
              onChange={(e) => setMinAmount(e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Max $"
              type="number"
              value={maxAmount}
              onChange={(e) => setMaxAmount(e.target.value)}
              size="small"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Orders Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Order ID</TableCell>
              <TableCell>Product</TableCell>
              <TableCell>Buyer</TableCell>
              <TableCell>Farmer</TableCell>
              <TableCell>Quantity</TableCell>
              <TableCell>Price/Unit</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Payment</TableCell>
              <TableCell>Delivery</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredOrders
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((order) => (
                <TableRow key={order.id} hover>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 100 }}>
                      {order.id.substring(0, 8)}...
                    </Typography>
                  </TableCell>
                  <TableCell>{order.product_name || 'N/A'}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{order.buyer_name || 'Unknown'}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {order.buyer_email}
                    </Typography>
                  </TableCell>
                  <TableCell>{order.farmer_name || 'N/A'}</TableCell>
                  <TableCell>
                    {Number(order.quantity || 0).toFixed(2)} {order.unit}
                  </TableCell>
                  <TableCell>${Number(order.price_per_unit || 0).toFixed(2)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold" color="success.main">
                      ${Number(order.total_amount || 0).toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={order.status}
                      size="small"
                      color={getStatusColor(order.status)}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={order.payment_status}
                      size="small"
                      color={getStatusColor(order.payment_status)}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={order.delivery_status}
                      size="small"
                      color={getStatusColor(order.delivery_status)}
                      icon={<LocalShipping />}
                    />
                  </TableCell>
                  <TableCell>
                    {format(new Date(order.order_date), 'MMM dd, yyyy')}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetails(order)}
                        color="primary"
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            {filteredOrders.length === 0 && (
              <TableRow>
                <TableCell colSpan={12} align="center">
                  <Typography variant="body2" color="textSecondary" py={4}>
                    No orders found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredOrders.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Order Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5">Order Details</Typography>
            <Typography variant="caption" color="textSecondary">
              {selectedOrder && format(new Date(selectedOrder.order_date), 'MMM dd, yyyy HH:mm')}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedOrder && (
            <Box>
              {/* Order Number & QR Code Section */}
              <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: 'primary.50', border: '2px solid', borderColor: 'primary.main' }}>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} sm={8}>
                    <Typography variant="caption" color="textSecondary" gutterBottom>
                      Order Number
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" color="primary" fontFamily="monospace" sx={{ letterSpacing: 2 }}>
                      {selectedOrder.order_number || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                      <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'white' }}>
                        <QRCodeSVG
                          value={JSON.stringify({
                            type: 'order',
                            id: selectedOrder.id,
                            number: selectedOrder.order_number,
                            timestamp: new Date().toISOString()
                          })}
                          size={120}
                          level="H"
                          includeMargin
                        />
                      </Paper>
                      <Typography variant="caption" color="textSecondary" align="center">
                        Scan for verification
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              <Divider sx={{ my: 3 }} />

              {/* Product & Parties Section */}
              <Box mb={3}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                  Product & Parties
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Product
                    </Typography>
                    <Typography variant="h6" color="primary">
                      {selectedOrder.product_name || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Buyer
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {selectedOrder.buyer_name || 'Unknown'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {selectedOrder.buyer_email}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Farmer
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {selectedOrder.farmer_name || 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Pricing Section */}
              <Box mb={3}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                  Pricing Details
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Quantity
                    </Typography>
                    <Typography variant="h6">
                      {Number(selectedOrder.quantity || 0).toFixed(2)} {selectedOrder.unit}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Price per Unit
                    </Typography>
                    <Typography variant="h6">
                      ${Number(selectedOrder.price_per_unit || 0).toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'success.50', border: '2px solid', borderColor: 'success.main' }}>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        Total Amount
                      </Typography>
                      <Typography variant="h4" color="success.main" fontWeight="bold">
                        ${Number(selectedOrder.total_amount || 0).toFixed(2)}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>

              {/* Status Section */}
              <Box mb={3}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                  Status Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Order Status
                    </Typography>
                    <Chip
                      label={selectedOrder.status}
                      color={getStatusColor(selectedOrder.status)}
                      sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Payment Status
                    </Typography>
                    <Chip
                      label={selectedOrder.payment_status}
                      color={getStatusColor(selectedOrder.payment_status)}
                      sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Delivery Status
                    </Typography>
                    <Chip
                      label={selectedOrder.delivery_status}
                      color={getStatusColor(selectedOrder.delivery_status)}
                      icon={<LocalShipping />}
                      sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Delivery Information */}
              {(selectedOrder.delivery_date || selectedOrder.delivery_address) && (
                <Box mb={3}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                    Delivery Information
                  </Typography>
                  <Grid container spacing={2}>
                    {selectedOrder.delivery_date && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Expected Delivery Date
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {format(new Date(selectedOrder.delivery_date), 'EEEE, MMM dd, yyyy')}
                        </Typography>
                      </Grid>
                    )}
                    {selectedOrder.delivery_address && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Delivery Address
                        </Typography>
                        <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                          <Typography variant="body1">{selectedOrder.delivery_address}</Typography>
                        </Paper>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}

              {/* Notes Section */}
              {selectedOrder.notes && (
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                    Additional Notes
                  </Typography>
                  <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="body1">{selectedOrder.notes}</Typography>
                  </Paper>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDetailsOpen(false)} variant="contained" size="large">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default AllOrders
